import * as THREE from 'three'
import { ImportedModelObjectMetadataService } from '../../metadata'
import {
  activateImportedModelTopologyHosts,
  disposeImportedModelTopologyHosts,
} from '../../topology/imported-model-topology-runtime'
import { hydrateImportedTopologySourceBrepRuntime } from '../../hydration'
import { applyGltfBrepBindingPlansToScene } from '../imported-brep-binding-plan'
import { applyGltfBodyEntriesToScene } from '../part'
import { analyzeCompleteGlbScene } from './analyzeCompleteGlbScene'
import { CompleteGlbDisplaySourceMetadataBinder } from './CompleteGlbDisplaySourceMetadataBinder'
import { CompleteGlbGeometryReuseDiagnostic } from './CompleteGlbGeometryReuseDiagnostic'
import { CompleteGlbResourceScopeBuilder } from './CompleteGlbResourceScopeBuilder'
import { buildImportedTopologySource } from '../topology-source/buildImportedTopologySource'
import { disposeImportedModelObjectTreeResources } from '../../render/imported-model-scene'
import type { ImportedModelResourceScope } from '../../resources/ImportedModelResourceScope'
import type {
  ComposedAssemblyResult,
  ImportedTopologySource,
  SceneBindingIndex,
  SelectionIndex,
} from '../types'

/**
 * 判断对象是否包含可由当前 occurrence 接管的可见 Mesh。
 *
 * @param topologySource 已按 occurrence scope 构建的拓扑源。
 * @returns 是否存在可见 mesh 目录项。
 */
function hasOccurrenceGeometry(topologySource: ImportedTopologySource): boolean {
  return Object.keys(topologySource.meshCatalog).length > 0
}

/**
 * 将原始场景对象绑定到最近的 manifest occurrence。
 *
 * @param root 原始 GLTF scene。
 * @param sourceObjectByNodeId manifest 身份到源对象的索引。
 * @param fileId 当前 GLB 文件 ID。
 * @returns 选择与场景绑定索引。
 */
function createSourceSceneIdentityIndexes(
  root: THREE.Object3D,
  sourceObjectByNodeId: ReadonlyMap<string, THREE.Object3D>,
  fileId: string,
): { selectionIndex: SelectionIndex; sceneBindings: SceneBindingIndex } {
  const nodeIdBySourceObject = new Map<THREE.Object3D, string>()
  sourceObjectByNodeId.forEach((object, nodeId) => {
    nodeIdBySourceObject.set(object, nodeId)
  })
  const selectionIndex: SelectionIndex = { byObject3DUuid: {} }
  const sceneBindings: SceneBindingIndex = {
    byAssemblyNodeId: {},
    byObject3DUuid: {},
  }

  const visit = (object: THREE.Object3D, inheritedAssemblyNodeId?: string): void => {
    const ownAssemblyNodeId = nodeIdBySourceObject.get(object)
    const assemblyNodeId = ownAssemblyNodeId ?? inheritedAssemblyNodeId
    if (assemblyNodeId) {
      ImportedModelObjectMetadataService.writeMetadata(object, {
        fileId,
        assemblyNodeId,
        selectionProxyObjectId: object.uuid,
        selectionTarget: { assemblyNodeId },
        /**
         * `sourceObjectByNodeId` 只包含分析器确认的 manifest 业务节点。
         * 精确命中的原始对象可以是 Group，也可以是单 primitive Mesh；后代虽然继承
         * assemblyNodeId 参与选择和拓扑定位，但不能继承位姿写入权限。
         */
        ...(ownAssemblyNodeId ? { componentTransformRoot: true } : {}),
      })
      selectionIndex.byObject3DUuid[object.uuid] = { assemblyNodeId }
      const objectIds = sceneBindings.byAssemblyNodeId[assemblyNodeId] ?? []
      if (!objectIds.includes(object.uuid)) {
        objectIds.push(object.uuid)
      }
      sceneBindings.byAssemblyNodeId[assemblyNodeId] = objectIds
      sceneBindings.byObject3DUuid[object.uuid] = object
    }
    object.children.forEach((child) => visit(child, assemblyNodeId))
  }
  visit(root)

  return { selectionIndex, sceneBindings }
}

/**
 * 完整 GLB 场景接管器。
 *
 * 该类只接受已解析 GLTF，并把 `gltf.scene` 本体作为最终结果根；不 clone、不拆分、
 * 不重建场景。每个语义 occurrence 拥有独立的拓扑索引和交互身份，definition 资产仍共享。
 */
export class CompleteGlbSceneImporter {
  /**
   * 接管完整 GLB 场景并建立 cad-core 运行时。
   *
   * @param input 完整场景、显式语义和 hydration 依赖。
   * @returns 可直接进入项目组合与持久化链路的稳定装配结果。
   */
  public async import(
    input: PuppyCAD.CompleteGlbSceneImportInput,
  ): Promise<ComposedAssemblyResult> {
    const activatedOccurrenceRoots: THREE.Object3D[] = []
    let resourceScope: ImportedModelResourceScope | null = null
    let stage: PuppyCAD.CompleteGlbImportStage = 'analyze-scene'
    let sourceAssemblyNodeId = 'none'

    try {
      input.assertCurrent?.()
      // parser association 只在原始 GLTF 结果上可靠，必须先于 topology host 等派生对象创建完成绑定。
      new CompleteGlbDisplaySourceMetadataBinder().bind(input.gltf)
      applyGltfBrepBindingPlansToScene(input.gltf)
      applyGltfBodyEntriesToScene(input.gltf)
      input.gltf.scene.updateMatrixWorld(true)

      stage = 'analyze-scene'
      const analysis = analyzeCompleteGlbScene({
        gltf: input.gltf,
        file: input.sourceGlbFile,
        version: input.version,
        expectedRootKind: input.expectedSemanticKind,
        occurrenceGraph: input.occurrenceGraph,
      })
      const manifest = analysis.manifest
      const manifestRootObject = analysis.sourceObjectByNodeId.get(manifest.rootNodeId)
      if (!manifestRootObject) {
        throw new Error(`完整 GLB 业务根缺少源场景对象：nodeId=${manifest.rootNodeId}`)
      }
      if (input.gltf.scene !== manifestRootObject) {
        /**
         * GLTFLoader 创建的 scene 只负责持有解析资源和承载业务子树，不代表独立 occurrence。
         * 项目组合后它会位于 OpenProject 节点与归档业务根之间；必须显式声明为运行时包装层，
         * 让保存链路折叠该层并保持父 occurrence 身份上下文，否则业务根会被误判为重复稳定节点。
         */
        ImportedModelObjectMetadataService.writeMetadata(input.gltf.scene, {
          runtimeWrapper: true,
        })
      }
      const importedTopologySourceByAssemblyNodeId = new Map<string, ImportedTopologySource>()

      for (const [nodeId, node] of Object.entries(manifest.nodesById)) {
        if (node.semanticKind !== 'assembly' && node.semanticKind !== 'part') {
          continue
        }
        sourceAssemblyNodeId = nodeId
        const occurrenceRoot = analysis.sourceObjectByNodeId.get(nodeId)
        if (!occurrenceRoot) {
          throw new Error(`完整 GLB occurrence 缺少源场景对象：nodeId=${nodeId}`)
        }

        input.assertCurrent?.()
        stage = 'build-topology-source'
        const topologySource: ImportedTopologySource = {
          ...buildImportedTopologySource(
            input.gltf.scene,
            {
              step: manifest.step,
              pmi: node.pmi,
              unitContext: manifest.unitContext,
            },
            {
              occurrenceRoot,
              ownedMeshes: analysis.ownedMeshesByNodeId.get(nodeId) ?? [],
              occurrenceNodeId: nodeId,
            },
          ),
          definitionNodeId: node.definitionNodeId,
        }
        if (!hasOccurrenceGeometry(topologySource)) {
          continue
        }

        stage = 'hydrate-brep-runtime'
        await hydrateImportedTopologySourceBrepRuntime(input.sourceGlbFile, topologySource, {
          projectFiles: [...input.projectFiles],
          readFileBytes: input.readFileBytes,
          brepRegistrar: input.brepRegistrar,
          importedBrepRuntimeCache: input.importedBrepRuntimeCache,
          assertCurrent: input.assertCurrent,
          logger: input.logger,
        })
        input.assertCurrent?.()
        stage = 'activate-topology-host'
        activateImportedModelTopologyHosts(occurrenceRoot, topologySource)
        activatedOccurrenceRoots.push(occurrenceRoot)
        importedTopologySourceByAssemblyNodeId.set(nodeId, topologySource)
      }

      stage = 'build-runtime-indexes'
      sourceAssemblyNodeId = 'none'
      const { selectionIndex, sceneBindings } = createSourceSceneIdentityIndexes(
        input.gltf.scene,
        analysis.sourceObjectByNodeId,
        input.sourceGlbFile.fileId,
      )
      /**
       * scene Group 继续作为运行时资源所有权根；设计树只从策略选中的真实业务 node 收集。
       * 项目组合层随后折叠这一层，因此不会同时显示 OpenProject 根和归档内部根。
       */
      const hierarchy = analysis.hierarchy
      new CompleteGlbGeometryReuseDiagnostic().reportIfEnabled(input.gltf)

      // 分析器已经完成 occurrence 到原始 mesh 的唯一归属划分，此时建立引用租约不会 clone 或拆分资源。
      resourceScope = new CompleteGlbResourceScopeBuilder().build({
        archiveRoot: input.gltf.scene,
        analysis,
      })
      input.assertCurrent?.()
      const result: ComposedAssemblyResult = {
        kind: 'composed-assembly',
        fileInfo: input.sourceGlbFile,
        partName: manifest.nodesById[manifest.rootNodeId]?.name || input.sourceGlbFile.fileId,
        root: input.gltf.scene,
        hierarchy,
        partAssetsByUuid: {},
        assemblyAssetsByUuid: {},
        manifest,
        selectionIndex,
        sceneBindings,
        warnings: [...analysis.warnings],
        sourceAssetKey: input.sourceAssetKey,
        importedTopologySourceByAssemblyNodeId,
        definitionNodeIdByAssemblyNodeId: new Map(
          Object.values(manifest.nodesById).map(node => [node.nodeId, node.definitionNodeId ?? node.nodeId]),
        ),
        persistenceStatus: 'stable',
        resourceScope,
      }
      resourceScope = null
      return result
    } catch (error) {
      for (let index = activatedOccurrenceRoots.length - 1; index >= 0; index -= 1) {
        disposeImportedModelTopologyHosts(activatedOccurrenceRoots[index])
      }
      // scope 建立前继续按对象树回收；建立后只归还 scope，避免重复销毁共享 GPU 资源。
      if (resourceScope) {
        resourceScope.release()
      } else {
        disposeImportedModelObjectTreeResources(input.gltf.scene)
      }
      throw new Error(
        [
          '完整 GLB 场景导入失败',
          `stage=${stage}`,
          `packageFileId=${input.packageFile.fileId}`,
          `packageUuid=${input.packageFile.uuid}`,
          `sourceGlbFileId=${input.sourceGlbFile.fileId}`,
          `persistedNodeId=${input.persistedNodeId?.trim() || 'none'}`,
          `sourceAssemblyNodeId=${sourceAssemblyNodeId}`,
        ].join(', '),
        { cause: error },
      )
    }
  }
}
